import React from 'react';
import { Grid, InputLabel, OutlinedInput } from '@mui/material';
import DefaultSelectClient from '../../../../components/default-select-client';
import DefaultUploadFile from '../../../../components/default-upload-file';
import { DateField, FieldError, FormSectionTitle } from '../../../../components/form-page-parts';
import { ValidationErrors } from '../../../../utils/validation';
import { LookupSelect, toLookupOptions } from '../../shared/lookup-select';
import { usePurchaseCreateEditState } from '../context/purchase-create-edit-context';
import { PurchaseField } from '../context/purchase-validation';
import { IPurchaseAddEditRequest } from '../../../../model/scp/purchase/request/purchase-add-edit-request';

interface PurchaseGeneralFormProps {
    errors: ValidationErrors<PurchaseField>;
    onChange: (e: any) => void;
    onDateChange: (field: keyof IPurchaseAddEditRequest, value: string | null) => void;
    onFileChange: (file: File) => void;
}

// Aba "Dados da Compra/Licitação": identificação, datas, descrição e (na inclusão) o documento inicial
export function PurchaseGeneralForm({ errors, onChange, onDateChange, onFileChange }: PurchaseGeneralFormProps) {
    const { purchaseRequest, purchaseId, file, modality, disputeMode, instrumentSummons, legalSupport, typeDocument, unidEntity } = usePurchaseCreateEditState();
    const isEdit = purchaseId !== undefined;
    const unitOptions = unidEntity.map((unit) => ({ value: unit.unitCode, label: `${unit.unitCode} - ${unit.name}` }));

    return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
                <FormSectionTitle title="Cliente" first />
                <DefaultSelectClient required handleOnChange={onChange} clientId={purchaseRequest.clientId} error={errors.clientId || false} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Identificação da Compra/Licitação" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LookupSelect required label="Instrumento Convocatório" name="select-type-calling-instrument" value={purchaseRequest.typeCallingInstrumentId} options={toLookupOptions(instrumentSummons)} onChange={onChange} error={errors.typeCallingInstrumentId} />
            </Grid>
            <Grid item xs={12} sm={6}>
                <LookupSelect required label="Modalidade" name="select-modality" value={purchaseRequest.modalityId} options={toLookupOptions(modality)} onChange={onChange} error={errors.modalityId} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Modo de Disputa" name="select-dispute-mode" value={purchaseRequest.disputeModeId} options={toLookupOptions(disputeMode)} onChange={onChange} error={errors.disputeModeId} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Amparo Legal" name="select-legal-support" value={purchaseRequest.legalSupportId} options={toLookupOptions(legalSupport, false)} onChange={onChange} error={errors.legalSupportId} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Unidade Compradora" name="select-cod-buyer-unit" value={purchaseRequest.codBuyerUnit} options={unitOptions} onChange={onChange} error={errors.codBuyerUnit} />
            </Grid>
            <Grid item xs={12} sm={4} lg={2}>
                <InputLabel required>Exercício</InputLabel>
                <OutlinedInput name="input-year-purchase" type="number" fullWidth autoComplete="off" value={purchaseRequest.yearPurchase || ''} onChange={onChange} error={!!errors.yearPurchase} />
                <FieldError message={errors.yearPurchase} />
            </Grid>
            <Grid item xs={12} sm={4} lg={2}>
                <InputLabel required>Número</InputLabel>
                <OutlinedInput name="input-number" fullWidth autoComplete="off" value={purchaseRequest.numberPurchase || ''} onChange={onChange} error={!!errors.numberPurchase} />
                <FieldError message={errors.numberPurchase} />
            </Grid>
            <Grid item xs={12} sm={4} lg={2}>
                <InputLabel>Número Processo</InputLabel>
                <OutlinedInput name="input-process-number" fullWidth autoComplete="off" value={purchaseRequest.processNumber || ''} onChange={onChange} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Datas" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <DateField label="Abertura de Propostas" value={purchaseRequest.proposalOpeningDate} onChange={(value) => onDateChange('proposalOpeningDate', value)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
                <DateField label="Encerramento de Propostas" value={purchaseRequest.proposalCloseDate} onChange={(value) => onDateChange('proposalCloseDate', value)} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Descrição" />
            </Grid>
            <Grid item xs={12}>
                <InputLabel required={!isEdit}>Título</InputLabel>
                <OutlinedInput name="input-title" fullWidth autoComplete="off" value={purchaseRequest.title || ''} onChange={onChange} disabled={isEdit} error={!!errors.title} />
                <FieldError message={errors.title} />
            </Grid>
            <Grid item xs={12}>
                <InputLabel required>Objeto</InputLabel>
                <OutlinedInput name="input-object" fullWidth autoComplete="off" multiline rows={4} value={purchaseRequest.objectPurchase || ''} onChange={onChange} error={!!errors.objectPurchase} />
                <FieldError message={errors.objectPurchase} />
            </Grid>
            <Grid item xs={12}>
                <InputLabel>Informações Complementares</InputLabel>
                <OutlinedInput name="input-information-complementary" fullWidth autoComplete="off" multiline rows={3} value={purchaseRequest.informationComplementary || ''} onChange={onChange} />
            </Grid>

            {!isEdit && (
                <React.Fragment>
                    <Grid item xs={12}>
                        <FormSectionTitle title="Documento" />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={4}>
                        <LookupSelect required label="Tipo de Documento" name="select-type-document" value={purchaseRequest.typeDocumentId} options={toLookupOptions(typeDocument)} onChange={onChange} error={errors.typeDocumentId} />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <InputLabel required>Arquivo</InputLabel>
                        <DefaultUploadFile props={{ id: 'file-upload-purchase', setFileUpload: onFileChange, file, extensions: ['pdf'], maxFileSizeMb: 30, error: !!errors.file }} />
                        <FieldError message={errors.file} />
                    </Grid>
                </React.Fragment>
            )}
        </Grid>
    );
}
